import React, {useState, useRef} from 'react';
import mpegts from "mpegts.js";
import Mpegts from "mpegts.js";
import styles from "./videoplayer.module.css";
import {MimeType} from "../../lib/m3u/m3u-model";
import {SelectedChannelContext} from "../../context/channel/SelectedChannelContext";
import {
    faPlay,
    faPause,
    faAlignLeft,
    faAlignRight,
    faStop, faUpRightAndDownLeftFromCenter
} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import IconButton from "../common/IconButton";
import logger from "../../lib/logger";


interface Props {
    url?: string;
    videoFormat: MimeType;
}

const videoPlayerConfig: Mpegts.Config = {
    enableWorker: true,
    liveBufferLatencyChasing: false, //False give better flow in the image. No loading indicator evry 5 seconds.
    liveBufferLatencyMaxLatency: 1.5,
    liveBufferLatencyMinRemain: 0.5,
    autoCleanupSourceBuffer: true,
    fixAudioTimestampGap: true,
}

export const VideoPlayer = ({url, videoFormat}: Props) => {
    const { removeSelectedChannel} = React.useContext(SelectedChannelContext);
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<Mpegts.Player | null>(null);
    const [isPaused, setIsPaused] = useState(false);
    const [isAlignRight, setIsAlignRight] = useState(false);
    const [videoSize, setVideoSize] = useState({ width: 500, height: 250 });
    const [resizeFrame, setResizeFrame] = useState({ width: 0, height: 0, top: 0, left: 0, visible: false });

    React.useEffect(() => {
        const mediaDataSource: Mpegts.MediaDataSource = {
            type: videoFormat, //'mp4', //'m2ts',  // could also be mpegts, m2ts, flv, mp4
            isLive: true,
            url: url
        }
        const player = mpegts.createPlayer(mediaDataSource, videoPlayerConfig);
        playerRef.current = player;

        if (!videoRef.current) {
            logger.error('VideoPlayer: videoRef is null, cannot attach media element');
            return;
        }
        player.attachMediaElement(videoRef.current);
        player.load();
        const playPromise = player.play();
        if (playPromise !== undefined) {
            playPromise.catch(error => {
                logger.warn('Auto-play was prevented:', error);
            });
        }

        player.on(Mpegts.Events.MEDIA_INFO, (mediaInfo: Mpegts.MSEPlayerMediaInfo) => {
            logger.debug('Media info:', mediaInfo);
        });

        player.on(Mpegts.Events.ERROR, (errorType: Mpegts.ErrorTypes, details: string, info: string) => {
            logger.error('Player error:', { errorType, details, info });
        });
        return function cleanup() {
            if (playerRef.current) {
                playerRef.current.destroy();
                playerRef.current = null;
            }
        };
    }, [url, videoFormat]);

    const togglePause = () => {
        setIsPaused(!isPaused)
        if (!isPaused) {
            videoRef.current?.pause()
        } else {
            videoRef.current?.play()
        }
    }

    const toggleAlign = () => {
        setIsAlignRight(!isAlignRight)
    }

    const startResize = (e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = videoSize.width;
        const startHeight = videoSize.height;
        const rect = videoRef.current?.getBoundingClientRect();

        setResizeFrame({
            width: startWidth,
            height: startHeight,
            top: rect?.top || 0,
            left: rect?.left || 0,
            visible: true
        });

        const onMouseMove = (e: MouseEvent) => {
            const deltaX = e.clientX - startX;
            const aspectRatio = startWidth / startHeight;

            const newWidth = Math.max(startWidth + deltaX, 100);
            const newHeight = Math.round(newWidth / aspectRatio);

            setResizeFrame(prev => ({
                ...prev,
                width: newWidth,
                height: newHeight,
            }));
        };

        const onMouseUp = (e: MouseEvent) => {
            const deltaX = e.clientX - startX;
            const aspectRatio = startWidth / startHeight;

            // Use the larger delta to determine the new size
            const newWidth = Math.max(startWidth + deltaX, 100);
            const newHeight = Math.round(newWidth / aspectRatio);

            setVideoSize({
                width: newWidth,
                height: newHeight
            });

            setResizeFrame({ width: 0, height: 0, top: 0, left:0, visible: false });

            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    return (
        <>
            <div className="align_horizontally">
                <IconButton onClick={removeSelectedChannel} type="button" tooltip="Stopp video"
                            icon={<FontAwesomeIcon icon={faStop} />} />

                <IconButton onClick={togglePause} type="button" tooltip={isPaused ? "Play" : "Pause"}
                            icon={isPaused ? <FontAwesomeIcon icon={faPlay} /> : <FontAwesomeIcon icon={faPause} />} />

                <IconButton onClick={toggleAlign} type="button" tooltip={isAlignRight ? "Align left" : "Align right"}
                            icon={isAlignRight ? <FontAwesomeIcon icon={faAlignLeft} /> : <FontAwesomeIcon icon={faAlignRight} />} />
            </div>

            <div className={`${styles.videoContainer} ${isAlignRight ? styles.alignRight : styles.alignLeft}`}>
                <video
                    controls
                    ref={videoRef}
                    id="videoElement"
                    style={{width: `${videoSize.width}px`, height: `${videoSize.height}px`}}>
                </video>
                <div
                    className={` ${styles.resizeHandleCorner}`}
                    onMouseDown={(e) => startResize(e)}>
                    <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} rotation={90} className={styles.resizeIcon}/>
                </div>
                {resizeFrame.visible && (
                    <div
                        className={styles.resizeFrame}
                        style={{
                            width: `${resizeFrame.width}px`,
                            height: `${resizeFrame.height}px`,
                            top: `${resizeFrame.top}px`,
                            left: `${resizeFrame.left}px`,
                        }}
                    >
                        {`${resizeFrame.width} x ${resizeFrame.height}`}
                    </div>
                )}

            </div>
        </>
    );
}

export default VideoPlayer;